/**
 * Cache-Busting Utilities for React SPA
 *
 * Provides robust cross-browser methods for forcing hard page reloads
 * that bypass browser cache. Research-verified to work on:
 * - Chrome/Edge (Chromium)
 * - Firefox
 * - Safari (Desktop & iOS)
 * - Mobile browsers
 *
 * Uses timestamp query parameters which are the most reliable method
 * as of 2024-2025 (location.reload(true) is deprecated and non-functional).
 *
 * Also clears browser Cache API storage to ensure stale scripts are removed.
 */

import { logger } from './logger';

/**
 * Clear all browser caches using the Cache API
 *
 * This clears:
 * - Service Worker caches
 * - Any CacheStorage entries created by the application
 *
 * Note: This does NOT clear the browser's HTTP cache, but combined with
 * no-cache headers and timestamp parameters, it provides comprehensive
 * cache invalidation.
 *
 * @returns Promise that resolves when caches are cleared
 */
export async function clearAllCaches(): Promise<void> {
  try {
    // Clear Cache API storage (Service Worker caches)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      logger.log(`[CacheUtils] Cleared ${cacheNames.length} cache(s)`);
    }

    // Unregister all service workers to prevent them from serving stale content
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
      if (registrations.length > 0) {
        logger.log(`[CacheUtils] Unregistered ${registrations.length} service worker(s)`);
      }
    }
  } catch (error) {
    // Non-fatal - continue with reload even if cache clearing fails
    logger.warn('[CacheUtils] Failed to clear caches:', error);
  }
}

/**
 * Force a hard page reload with cache busting
 *
 * This method is the most reliable cross-browser approach for forcing
 * a full page reload that bypasses all browser caches. It works by:
 * 1. Clearing all Cache API storage (service worker caches)
 * 2. Adding a timestamp query parameter to the URL
 * 3. Navigating to the new URL via window.location.href
 * 4. Browser treats this as a new page request, bypassing cache
 *
 * The cache clearing step ensures any service worker cached content
 * is removed before the reload, preventing stale scripts from being served.
 *
 * @param preserveQueryParams - Keep existing query params (default: false)
 * @param preserveHash - Keep URL hash fragment (default: false)
 *
 * @example
 * // Clean reload (remove all params and hash)
 * forceHardReload();
 *
 * @example
 * // Preserve current route params
 * forceHardReload(true, true);
 */
export function forceHardReload(preserveQueryParams: boolean = false, preserveHash: boolean = false): void {
  // Clear caches first, then reload (fire and forget - don't wait)
  clearAllCaches().finally(() => {
    try {
      const timestamp = Date.now();
      const url = new URL(window.location.href);

      // Remove or preserve existing query parameters
      if (!preserveQueryParams) {
        // Clear all existing params
        url.search = '';
      }

      // Add cache-busting timestamp
      url.searchParams.set('_t', timestamp.toString());

      // Remove or preserve hash
      if (!preserveHash) {
        url.hash = '';
      }

      // Perform reload - this is the most reliable cross-browser method
      window.location.href = url.toString();
    } catch (error) {
      logger.error('Force reload failed:', error);
      // Fallback to origin if URL manipulation fails
      window.location.href = window.location.origin;
    }
  });
}


/**
 * Clean up cache-bust timestamp parameter after reload
 *
 * After a force reload, the URL will contain a ?_t=timestamp parameter.
 * Call this function in your app's root component to clean up the URL
 * without triggering another page load.
 *
 * @example
 * // In App.tsx useEffect
 * useEffect(() => {
 *   cleanupCacheBustParam();
 * }, []);
 */
export function cleanupCacheBustParam(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_t')) {
      url.searchParams.delete('_t');
      // Use replaceState to update URL without reload
      window.history.replaceState({}, '', url.toString());
    }
  } catch (error) {
    logger.error('Cleanup cache-bust param failed:', error);
  }
}

/**
 * Force reload with progressive fallback
 *
 * Implements a multi-attempt strategy with different methods.
 * Tracks attempts in sessionStorage to prevent infinite loops.
 * Clears all caches before each attempt.
 *
 * Attempt sequence:
 * 1. Clear caches + Timestamp query param (best method)
 * 2. Clear caches + Replace with timestamp (if navigate fails)
 * 3. Clear caches + Standard reload (last resort)
 * 4. Show user manual refresh instructions
 *
 * @example
 * // Use in critical error recovery scenarios
 * forceHardReloadWithFallback();
 */
export function forceHardReloadWithFallback(): void {
  const attemptKey = 'reload_attempt';
  const maxAttempts = 3;

  // sessionStorage can throw in private mode / under corporate policy. A storage
  // failure must degrade gracefully: treat the attempt count as 0 and still
  // attempt a reload rather than throwing before any recovery happens.
  let attempt = 0;
  try {
    attempt = parseInt(sessionStorage.getItem(attemptKey) || '0', 10);
  } catch {
    attempt = 0;
  }

  if (attempt >= maxAttempts) {
    // All automatic methods failed - show user message
    try {
      sessionStorage.removeItem(attemptKey);
    } catch {
      // ignore storage failures
    }
    alert(
      'Unable to reload the application automatically.\n\n' +
        'Please manually refresh your browser:\n' +
        '• Windows/Linux: Press Ctrl+Shift+R\n' +
        '• Mac: Press Cmd+Shift+R'
    );
    return;
  }

  try {
    sessionStorage.setItem(attemptKey, String(attempt + 1));
  } catch {
    // Storage unavailable: proceed with the reload anyway. The loop guard is
    // best-effort; a single reload attempt is preferable to throwing.
  }

  // Clear caches before attempting reload
  clearAllCaches().finally(() => {
    try {
      // Progressive fallback chain
      switch (attempt) {
        case 0: {
          // Method 1: Timestamp in query param (BEST)
          const url = new URL(window.location.href);
          url.searchParams.set('_t', Date.now().toString());
          window.location.href = url.toString();
          break;
        }

        case 1: {
          // Method 2: Force replace with timestamp
          const url2 = new URL(window.location.href);
          url2.searchParams.set('_t', Date.now().toString());
          window.location.replace(url2.toString());
          break;
        }

        case 2:
          // Method 3: Last resort - standard reload
          window.location.reload();
          break;
      }
    } catch (error) {
      logger.error('Fallback reload failed:', error);
      // Final fallback - navigate to origin
      window.location.href = window.location.origin;
    }
  });
}
